import { cn } from "@/shared/lib/cn";
import {
	Divider,
	type DividerProps,
	InputBox,
	type InputBoxProps,
	Badge as PrimitiveBadge,
	type BadgeProps as PrimitiveBadgeProps,
	Button as PrimitiveButton,
	type ButtonProps as PrimitiveButtonProps,
	Spinner as PrimitiveSpinner,
	type SpinnerProps as PrimitiveSpinnerProps,
	TextareaBox,
	type TextareaBoxProps,
} from "@/shared/ui/primitives";
import {
	Children,
	type ButtonHTMLAttributes,
	type HTMLAttributes,
	type InputHTMLAttributes,
	type LabelHTMLAttributes,
	type ReactNode,
	type SelectHTMLAttributes,
	type TdHTMLAttributes,
	type ThHTMLAttributes,
	createContext,
	isValidElement,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";

// ── Card ──

export const Card = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
	<div
		className={cn(
			"w-full rounded-[24px] border border-outline bg-(--surface) p-[24px] text-ct-strong",
			className,
		)}
		{...props}
	/>
);

export const CardHeader = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
	<div className={cn("flex w-full flex-col items-start gap-[8px]", className)} {...props} />
);

export const CardTitle = ({ className, ...props }: HTMLAttributes<HTMLHeadingElement>) => (
	<h3
		className={cn("text-[24px] font-semibold tracking-[-0.02em] text-ct-strong", className)}
		{...props}
	/>
);

export const CardDescription = ({ className, ...props }: HTMLAttributes<HTMLParagraphElement>) => (
	<p className={cn("text-sm leading-[1.6] text-ct-regular", className)} {...props} />
);

export const CardContent = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
	<div className={cn("flex w-full flex-col items-start gap-[16px]", className)} {...props} />
);

export const CardFooter = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
	<div
		className={cn("flex w-full items-center justify-between gap-[12px]", className)}
		{...props}
	/>
);

// ── Button / Badge / Input / Separator / Spinner / Textarea ──

export const Button = (props: PrimitiveButtonProps) => (
	<PrimitiveButton variant="primary" {...props} />
);

export const Badge = (props: PrimitiveBadgeProps) => <PrimitiveBadge {...props} />;

export const Input = ({ className, type, ...props }: InputBoxProps) => {
	const isDateLike =
		type === "date" || type === "time" || type === "month" || type === "datetime-local";
	return (
		<InputBox
			className={cn("min-w-0 max-w-full", isDateLike && "appearance-none", className)}
			type={type}
			{...props}
		/>
	);
};

export const Textarea = (props: TextareaBoxProps) => <TextareaBox {...props} />;

export const Spinner = (props: PrimitiveSpinnerProps) => <PrimitiveSpinner {...props} />;

export const Separator = ({ className, ...props }: DividerProps) => (
	<Divider className={cn("h-[1px] w-full bg-divider", className)} {...props} />
);

// ── Label ──

export const Label = ({ className, ...props }: LabelHTMLAttributes<HTMLLabelElement>) => (
	<label
		className={cn("text-sm font-medium text-ct-strong leading-none", className)}
		{...props}
	/>
);

// ── Table ──

export const Table = ({ className, ...props }: HTMLAttributes<HTMLTableElement>) => (
	<div className="w-full overflow-auto">
		<table className={cn("w-full caption-bottom text-sm", className)} {...props} />
	</div>
);

export const TableHeader = ({ className, ...props }: HTMLAttributes<HTMLTableSectionElement>) => (
	<thead className={cn("[&_tr]:border-b [&_tr]:border-outline", className)} {...props} />
);

export const TableBody = ({ className, ...props }: HTMLAttributes<HTMLTableSectionElement>) => (
	<tbody className={cn("[&_tr:last-child]:border-0", className)} {...props} />
);

export const TableRow = ({ className, ...props }: HTMLAttributes<HTMLTableRowElement>) => (
	<tr
		className={cn("border-b border-outline transition-colors hover:bg-bg-regular/40", className)}
		{...props}
	/>
);

export const TableHead = ({ className, ...props }: ThHTMLAttributes<HTMLTableCellElement>) => (
	<th
		className={cn(
			"h-[40px] px-[12px] text-left align-middle text-xs font-medium text-ct-regular",
			className,
		)}
		{...props}
	/>
);

export const TableCell = ({ className, ...props }: TdHTMLAttributes<HTMLTableCellElement>) => (
	<td
		className={cn("px-[12px] py-[10px] align-middle text-sm text-ct-strong", className)}
		{...props}
	/>
);

// ── Tabs ──

const TabsContext = createContext<{ value: string; onChange: (v: string) => void }>({
	value: "",
	onChange: () => {},
});

export const Tabs = ({
	defaultValue,
	value: controlledValue,
	onValueChange,
	className,
	children,
	...props
}: HTMLAttributes<HTMLDivElement> & {
	defaultValue?: string;
	value?: string;
	onValueChange?: (value: string) => void;
}) => {
	const [internalValue, setInternalValue] = useState(defaultValue ?? "");
	const value = controlledValue ?? internalValue;
	const onChange = onValueChange ?? setInternalValue;

	return (
		<TabsContext.Provider value={{ value, onChange }}>
			<div className={cn("w-full", className)} {...props}>
				{children}
			</div>
		</TabsContext.Provider>
	);
};

export const TabsList = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
	<div
		className={cn(
			"inline-flex items-center gap-[4px] rounded-[12px] bg-(--surface) border border-outline p-[4px]",
			className,
		)}
		{...props}
	/>
);

export const TabsTrigger = ({
	value,
	className,
	...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { value: string }) => {
	const ctx = useContext(TabsContext);
	const isActive = ctx.value === value;

	return (
		<button
			type="button"
			className={cn(
				"px-[12px] py-[6px] rounded-[8px] text-sm font-medium transition-colors",
				isActive
					? "bg-bg-regular text-ct-strong"
					: "text-ct-regular hover:text-ct-strong",
				className,
			)}
			onClick={() => ctx.onChange(value)}
			{...props}
		/>
	);
};

export const TabsContent = ({
	value,
	className,
	...props
}: HTMLAttributes<HTMLDivElement> & { value: string }) => {
	const ctx = useContext(TabsContext);
	if (ctx.value !== value) return null;

	return <div className={cn("mt-[8px]", className)} {...props} />;
};

// ── Select ──

export const Select = ({
	value,
	onChange,
	children,
	className,
}: SelectHTMLAttributes<HTMLSelectElement>) => {
	const [isOpen, setIsOpen] = useState(false);
	const rootRef = useRef<HTMLDivElement>(null);

	const options: { value: string; label: ReactNode }[] = [];
	Children.forEach(children, (child) => {
		if (isValidElement(child)) {
			const p = child.props as { value?: string; children?: ReactNode };
			if (p.value !== undefined) {
				options.push({ value: p.value, label: p.children });
			}
		}
	});

	const selectedOption = options.find((o) => o.value === String(value));

	const bgClass =
		className
			?.split(/\s+/)
			.filter((c) => c.startsWith("bg-"))
			.join(" ") || "";
	const wrapperClass =
		className
			?.split(/\s+/)
			.filter((c) => !c.startsWith("bg-"))
			.join(" ") || "";

	useEffect(() => {
		if (!isOpen) return;
		const handler = (e: PointerEvent) => {
			if (!rootRef.current?.contains(e.target as Node)) setIsOpen(false);
		};
		document.addEventListener("pointerdown", handler, true);
		return () => document.removeEventListener("pointerdown", handler, true);
	}, [isOpen]);

	return (
		<div ref={rootRef} className={cn("relative w-full", wrapperClass)}>
			<button
				type="button"
				className={cn(
					"w-full rounded-[10px] border border-outline bg-bg-regular p-[14px] text-sm text-ct-strong outline-none transition-colors",
					bgClass,
				)}
				style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between", gap: 8 }}
				onClick={() => setIsOpen((v) => !v)}
			>
				<span className="truncate text-left">{selectedOption?.label ?? value}</span>
				<svg
					width="12"
					height="12"
					viewBox="0 0 12 12"
					style={{ flexShrink: 0 }}
					className={cn(
						"text-ct-regular transition-transform",
						isOpen && "rotate-180",
					)}
				>
					<path
						d="M3 4.5L6 7.5L9 4.5"
						stroke="currentColor"
						strokeWidth="1.5"
						fill="none"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
				</svg>
			</button>
			{isOpen && (
				<div
					className="absolute z-[9999] mt-[4px] w-full rounded-[10px] bg-(--surface) border border-outline shadow-lg p-[6px] max-h-[240px] overflow-auto flex flex-col gap-[2px]"
					style={{ scrollbarWidth: "thin", scrollbarColor: "var(--bg-strong) transparent" }}
				>
					{options.map((opt) => {
						const selected = opt.value === String(value);
						return (
							<button
								key={opt.value}
								type="button"
								className={cn(
									"w-full px-[10px] py-[8px] rounded-[8px] text-sm text-left transition-colors",
									selected
										? "bg-bg-regular text-ct-strong font-medium"
										: "text-ct-regular hover:bg-bg-regular hover:text-ct-strong",
								)}
								onClick={() => {
									onChange?.({ target: { value: opt.value } } as any);
									setIsOpen(false);
								}}
							>
								{opt.label}
							</button>
						);
					})}
				</div>
			)}
		</div>
	);
};

export const SelectOption = (
	_props: HTMLAttributes<HTMLOptionElement> & { value?: string; disabled?: boolean },
) => null;

// ── Switch ──

export const Switch = ({
	checked,
	onCheckedChange,
	className,
	...props
}: Omit<ButtonHTMLAttributes<HTMLButtonElement>, "onChange"> & {
	checked?: boolean;
	onCheckedChange?: (checked: boolean) => void;
}) => (
	<button
		type="button"
		role="switch"
		aria-checked={checked}
		className={cn(
			"relative inline-flex h-[24px] w-[44px] shrink-0 cursor-pointer rounded-full transition-colors",
			checked ? "bg-pm-strong" : "bg-bg-regular",
			className,
		)}
		onClick={() => onCheckedChange?.(!checked)}
		{...props}
	>
		<span
			className={cn(
				"pointer-events-none block h-[20px] w-[20px] rounded-full bg-white shadow-sm transition-transform mt-[2px]",
				checked ? "translate-x-[22px]" : "translate-x-[2px]",
			)}
		/>
	</button>
);

// ── Checkbox ──

export const Checkbox = ({
	checked,
	onCheckedChange,
	className,
	...props
}: Omit<InputHTMLAttributes<HTMLInputElement>, "onChange" | "type"> & {
	onCheckedChange?: (checked: boolean) => void;
}) => (
	<input
		type="checkbox"
		checked={checked}
		onChange={(e) => onCheckedChange?.(e.target.checked)}
		className={cn(
			"h-[16px] w-[16px] rounded-[4px] border border-outline accent-pm-strong cursor-pointer",
			className,
		)}
		{...props}
	/>
);

// ── Progress ──

export const Progress = ({
	value = 0,
	max = 100,
	className,
	...props
}: HTMLAttributes<HTMLDivElement> & { value?: number; max?: number }) => (
	<div
		className={cn("relative h-[8px] w-full overflow-hidden rounded-full bg-bg-regular", className)}
		{...props}
	>
		<div
			className="h-full rounded-full bg-pm-strong transition-all"
			style={{ width: `${Math.min(100, (value / max) * 100)}%` }}
		/>
	</div>
);

// ── Dialog ──

export const Dialog = ({
	open,
	onOpenChange,
	children,
}: {
	open?: boolean;
	onOpenChange?: (open: boolean) => void;
	children?: React.ReactNode;
}) => {
	if (!open) return null;

	return (
		<div className="fixed inset-0 z-120 flex items-center justify-center px-[16px]">
			<div
				className="fixed inset-0 bg-black/40 backdrop-blur-sm"
				onClick={() => onOpenChange?.(false)}
				onKeyDown={() => {}}
			/>
			<div className="relative z-140 w-full max-w-[480px]">{children}</div>
		</div>
	);
};

export const DialogContent = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
	<div
		className={cn(
			"w-full rounded-[24px] bg-(--surface) border border-outline p-[24px]",
			className,
		)}
		{...props}
	/>
);

export const DialogHeader = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
	<div className={cn("flex flex-col items-start gap-[4px]", className)} {...props} />
);

export const DialogTitle = ({ className, ...props }: HTMLAttributes<HTMLHeadingElement>) => (
	<h3 className={cn("text-lg font-semibold text-ct-strong", className)} {...props} />
);

export const DialogDescription = ({
	className,
	...props
}: HTMLAttributes<HTMLParagraphElement>) => (
	<p className={cn("text-sm text-ct-regular", className)} {...props} />
);

export const DialogFooter = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
	<div
		className={cn("flex items-center justify-end gap-[8px] pt-[16px]", className)}
		{...props}
	/>
);

// ── Alert ──

export const Alert = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
	<div
		className={cn(
			"w-full rounded-[16px] border border-outline bg-bg-regular/40 p-[16px]",
			className,
		)}
		{...props}
	/>
);

export const AlertTitle = ({ className, ...props }: HTMLAttributes<HTMLHeadingElement>) => (
	<h5 className={cn("text-sm font-semibold text-ct-strong", className)} {...props} />
);

export const AlertDescription = ({ className, ...props }: HTMLAttributes<HTMLParagraphElement>) => (
	<p className={cn("text-sm text-ct-regular mt-[4px]", className)} {...props} />
);

// ── Avatar ──

export const Avatar = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
	<div
		className={cn(
			"relative flex h-[40px] w-[40px] shrink-0 overflow-hidden rounded-full",
			className,
		)}
		{...props}
	/>
);

export const AvatarImage = ({
	className,
	...props
}: HTMLAttributes<HTMLImageElement> & { src?: string; alt?: string }) => (
	<img className={cn("aspect-square h-full w-full object-cover", className)} {...props} />
);

export const AvatarFallback = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
	<div
		className={cn(
			"flex h-full w-full items-center justify-center rounded-full bg-bg-regular text-sm font-medium text-ct-regular",
			className,
		)}
		{...props}
	/>
);
